import { FC, useState } from 'react'
import ReactModal from 'react-modal'
import { useDispatch } from 'react-redux'
import { Capacitor } from '@capacitor/core'
import { Chip } from 'merinio-mui'

import { ReactComponent as CrossIcon } from 'assets/images/crossIcon.svg'
import { closeModal } from 'store/actions/modals'
import { getProjectName } from 'services/Utilities'
import { toggleResourceSelection } from 'store/actions/tables'
import {
  AdditionalInfoForm,
  BlockedUsersForm,
  LockedUsersForm,
  NotesForm,
  PositionsForm,
  PreferredAttributesForm,
  PreferredUsersForm,
  ProjectOwnersForm,
  RequiredAttributesForm,
  DefaultShiftTags,
  StatusForm,
  Clients
} from './Forms'
import { ProjectFormsProps } from './types'

import { t } from 'translations'
import { insertIf } from 'common'
import hasRessources from 'services/utilFunctions/hasRessources'

const ProjectBulkEditModal: FC<ProjectFormsProps> = ({ type, projectIds }) => {
  const dispatch = useDispatch()
  const [selectedProjects, setSelectedProjects] = useState(projectIds)
  const [formType, setFormType] = useState<ProjectFormsProps['type']>(type || '')
  const handleDelete = (id: string) => {
    toggleResourceSelection('projects', id)
    setSelectedProjects(prev => [...prev].filter(prevId => prevId !== id))
  }

  const typeOptions: ProjectFormsProps['type'][] = [
    'additionalInfo',
    'notes',
    ...(insertIf(hasRessources('positions'), 'positions') as ProjectFormsProps['type'][]),
    ...(insertIf(hasRessources('attributes'), 'preferredAttributes', 'requiredAttributes') as ProjectFormsProps['type'][]),
    'projectOwners',
    'status',
    'preferredUsers',
    'blockedUsers',
    'lockedUsers',
    ''
  ]

  return (
    <ReactModal
      isOpen
      className={`merinio-modal`}
      overlayClassName={`merinio-modal-overlay${Capacitor.isNativePlatform() ? ' is-mobile-app' : ''}`}
      shouldFocusAfterRender
      onRequestClose={() => dispatch(closeModal())}
      shouldCloseOnOverlayClick={false}
    >
      {
        <div className="w-100 h-100 d-flex flex-column">
          <div className="flex-fill">
            <>
              <h4>
                {t(`manager.users.bulkEdit.TITLE`)} {formType ? `- ${t(`manager.projects.form.${formType.toUpperCase()}.LABEL`)}` : ''}
              </h4>
              {t(`manager.users.bulkEdit.SUMMARY`) && <div>{t(`manager.projects.bulkEdit.SUMMARY`)}</div>}
            </>
            <div className=" mt-2 mb-4 p-3 border rounded bg-light" style={{ maxHeight: '10rem', overflow: 'auto' }}>
              <div className="d-flex" style={{ flexWrap: 'wrap', gap: '0.3em' }}>
                {selectedProjects.map(id => (
                  <Chip
                    key={id}
                    label={getProjectName(id)}
                    onDelete={() => handleDelete(id)}
                    deleteIcon={<DeleteButton onDelete={() => handleDelete(id)} />}
                    sx={{
                      borderRadius: '2px',
                      height: 'auto',
                      '& .MuiChip-label': {
                        display: 'flex',
                        padding: '3px 3px 3px 6px',
                        fontSize: '0.85rem'
                      }
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
          {!type && (
            <div className="mb-3">
              <div>{t('manager.users.bulkEdit.TYPE.LABEL')}</div>
              <select className="custom-select my-2" value={formType} onChange={event => setFormType(event.target.value as ProjectFormsProps['type'])}>
                {typeOptions.map((type, index) => (
                  <option key={index} value={type}>
                    {type ? t(`manager.projects.form.${type.toUpperCase()}.LABEL`) : t(`manager.projects.form.SELECT_TYPE`)}
                  </option>
                ))}
              </select>
            </div>
          )}
          <AdditionalInfoForm type={formType} projectIds={selectedProjects} />
          <BlockedUsersForm type={formType} projectIds={selectedProjects} />
          <LockedUsersForm type={formType} projectIds={selectedProjects} />
          <NotesForm type={formType} projectIds={selectedProjects} />
          <PositionsForm type={formType} projectIds={selectedProjects} />
          <PreferredAttributesForm type={formType} projectIds={selectedProjects} />
          <PreferredUsersForm type={formType} projectIds={selectedProjects} />
          <ProjectOwnersForm type={formType} projectIds={selectedProjects} />
          <RequiredAttributesForm type={formType} projectIds={selectedProjects} />
          <StatusForm type={formType} projectIds={selectedProjects} />
          <DefaultShiftTags type={formType} projectIds={selectedProjects} />
          <Clients type={formType} projectIds={selectedProjects} />
        </div>
      }
    </ReactModal>
  )
}

const DeleteButton = ({ onDelete }) => {
  const [hover, setHover] = useState(false)

  return (
    <div
      className={`d-flex align-items-center px-1`}
      style={{ backgroundColor: hover ? '#FFBDAD' : '', height: '26.39px', fill: hover ? '#DE350B' : '' }}
      role="button"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onDelete}
    >
      <CrossIcon />
    </div>
  )
}

export default ProjectBulkEditModal
